import React from "react";
import { Box } from "@mui/material";
import NavbarSection from "./sections/NavbarSection";
import HeroSection from "./sections/HeroSection";
import SpotlightSection from "./sections/SpotlightSection";
import FeaturedProductsSection from "./sections/FeaturedProductsSection";
import BrandsSection from "./sections/BrandsSection";
import CategoriesSection from "./sections/CategoriesSection";
import AboutSection from "./sections/AboutSection";
import HighlightsSection from "./sections/HighlightsSection";
import ContactSection from "./sections/ContactSection";
import FooterSection from "./sections/FooterSection";
import FloatingWhatsappSection from "./sections/FloatingWhatsappSection";
import { resolveLandingAssetUrl } from "../../../utils/landingAssets";

function safeCssText(value) {
  return String(value || "").replace(/[{};\\"']/g, "").trim();
}

function LandingFonts({ theme }) {
  const googleFontsUrl = String(theme.googleFontsUrl || "").trim();

  React.useEffect(() => {
    const id = "webprofit-landing-editor-google-fonts";
    let link = document.getElementById(id);

    if (!googleFontsUrl) {
      link?.remove();
      return undefined;
    }

    if (!link) {
      link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.href = googleFontsUrl;
    return undefined;
  }, [googleFontsUrl]);

  const faces = (Array.isArray(theme.fonts) ? theme.fonts : [])
    .map((font) => {
      const family = safeCssText(font.family);
      const url = resolveLandingAssetUrl(font.url);
      if (!family || !url) return "";
      const weight = safeCssText(font.weight || "400");
      const style = ["normal", "italic", "oblique"].includes(font.style) ? font.style : "normal";
      const format = font.format === "woff" ? "woff" : "woff2";
      return `@font-face{font-family:"${family}";src:url("${url}") format("${format}");font-weight:${weight};font-style:${style};font-display:swap;}`;
    })
    .filter(Boolean)
    .join("\n");

  return faces ? <style>{faces}</style> : null;
}

export default function LandingCanvas({
  form,
  activeSection,
  device,
  productsEnabled,
  onSelectSection,
  onCommit,
  onCommitMany,
  onUploadImage,
  onRemoveImage,
  saving,
}) {
  const bodyFont = form.theme.bodyFontFamily || form.theme.fontFamily || "Arial";
  const headingFont = form.theme.headingFontFamily || form.theme.fontFamily || bodyFont;
  const shared = {
    form,
    device,
    productsEnabled,
    onSelect: onSelectSection,
    onCommit,
    onCommitMany,
    onUploadImage,
    onRemoveImage,
    disabled: saving,
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: device === "mobile" ? 390 : "100%",
        maxWidth: device === "mobile" ? "100%" : 1440,
        mx: "auto",
        bgcolor: form.theme.surfaceColor || "#fff",
        color: form.theme.textColor || "#1A1A2E",
        fontFamily: `${bodyFont}, sans-serif`,
        "& .MuiTypography-root, & button, & input, & textarea": { fontFamily: `${bodyFont}, sans-serif` },
        "& h1, & h2, & h3, & h4, & h5, & h6": { fontFamily: `${headingFont}, sans-serif` },
        borderRadius: device === "mobile" ? 4 : Math.max(0, Number(form.theme.borderRadius || 10) / 5),
        overflow: "hidden",
        boxShadow: "0 28px 70px rgba(25,36,47,.18)",
        border: `1px solid ${form.theme.dividerColor || "rgba(25,36,47,.12)"}`,
      }}
    >
      <LandingFonts theme={form.theme} />
      <NavbarSection {...shared} active={activeSection === "navbar"} />
      <HeroSection {...shared} active={activeSection === "hero"} />
      <SpotlightSection {...shared} active={activeSection === "spotlight"} />
      <FeaturedProductsSection {...shared} active={activeSection === "featured"} />
      <BrandsSection {...shared} active={activeSection === "brands"} />
      <CategoriesSection {...shared} active={activeSection === "categories"} />
      <AboutSection {...shared} active={activeSection === "about"} />
      <HighlightsSection {...shared} active={activeSection === "highlights"} />
      <ContactSection {...shared} active={activeSection === "contact"} />
      <FooterSection {...shared} active={activeSection === "footer"} />
      <FloatingWhatsappSection {...shared} active={activeSection === "floatingWhatsapp"} />
    </Box>
  );
}
